import { defineStore } from "pinia";
import { ref } from "vue";
import { supabase } from "../lib/supabase";
import { matchesFotoContextLabel, matchesFotoContextPrefix, normalizeFotoContextLabel } from "../utils/fotoContextLabels";
import { resolveStorageUrl } from "../utils/storageUrl";
import { useToast } from "../composables/useToast";
import { captureError } from "../lib/sentry";
import type { Rapport, RapportWithOfferte, RealtimePayload } from "../../../../types";
import type { RealtimeChannel } from "@supabase/supabase-js";
import type { HerstelBericht } from "../../../../types/shared/common";

interface RapportDetailFoto {
  id: string;
  remote_url: string | null;
  context_label: string;
  sort_order?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
  locatie_id?: string | null;
  isFallback?: boolean;
}

export const useRapportenStore = defineStore("rapporten", () => {
  const rapporten = ref<RapportWithOfferte[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const currentRapport = ref<RapportWithOfferte | null>(null);
  const toast = useToast();
  let realtimeSubscription: RealtimeChannel | null = null;
  let constateringenSubscription: RealtimeChannel | null = null;
  const suppressRefetchUntilByRapportId = new Map<string, number>();

  const suppressRealtimeRefetch = (rapportId: string, durationMs = 2000) => {
    suppressRefetchUntilByRapportId.set(rapportId, Date.now() + durationMs);
  };

  const isLocatieFotoLabel = (label: string | null | undefined) => {
    return (
      matchesFotoContextLabel(label, "foto_boven") ||
      matchesFotoContextPrefix(label, "foto_overzicht") ||
      matchesFotoContextPrefix(label, "foto_pv_overzicht")
    );
  };

  const isInstallatieFotoLabel = (label: string | null | undefined) => {
    return matchesFotoContextPrefix(label, "installatie_foto") || matchesFotoContextLabel(label, "installatie_foto_pv");
  };

  const isDetailFotoLabel = (label: string | null | undefined) => {
    return isLocatieFotoLabel(label) || isInstallatieFotoLabel(label);
  };

  const isRapportLevelFoto = (row: any) => {
    return (
      !row?.verdeler_id &&
      !row?.constatering_id &&
      !row?.omvormer_id &&
      !row?.meldformulier_id &&
      !row?.internal_locatie_id
    );
  };

  const sortDetailFotos = (left: RapportDetailFoto, right: RapportDetailFoto) => {
    const leftIsPv = matchesFotoContextLabel(left.context_label, "installatie_foto_pv");
    const rightIsPv = matchesFotoContextLabel(right.context_label, "installatie_foto_pv");

    if (leftIsPv !== rightIsPv) {
      return leftIsPv ? 1 : -1;
    }

    const leftSortOrder = left.sort_order ?? Number.MAX_SAFE_INTEGER;
    const rightSortOrder = right.sort_order ?? Number.MAX_SAFE_INTEGER;
    if (leftSortOrder !== rightSortOrder) {
      return leftSortOrder - rightSortOrder;
    }

    return (left.created_at || "").localeCompare(right.created_at || "");
  };

  const normalizeDetailFoto = (row: any): RapportDetailFoto => ({
    id: String(row.id),
    remote_url: row.remote_url ? resolveStorageUrl(row.remote_url) : row.remote_url,
    context_label: normalizeFotoContextLabel(row.context_label),
    sort_order: row.sort_order ?? null,
    created_at: row.created_at ?? null,
    updated_at: row.updated_at ?? null,
    locatie_id: row.locatie_id ?? null,
    isFallback: Boolean(row.isFallback),
  });

  const getRapportLocatieId = (rapport: any): string | null => {
    return rapport?.locatie_id || rapport?.offerte?.locatie_id || rapport?.offertes?.locatie_id || rapport?.locatie_snapshot?.id || null;
  };

  const getLegacyInstallatieFotos = (installatie: Record<string, any> | null | undefined): RapportDetailFoto[] => {
    if (!installatie) return [];

    const candidates = [
      {
        id: "legacy-installatie-foto-1",
        context_label: "installatie_foto_1",
        remote_url: installatie.installatieFoto1Online || installatie.installatieFoto1 || null,
        sort_order: 0,
      },
      {
        id: "legacy-installatie-foto-2",
        context_label: "installatie_foto_2",
        remote_url: installatie.installatieFoto2Online || installatie.installatieFoto2 || null,
        sort_order: 1,
      },
      {
        id: "legacy-installatie-foto-pv",
        context_label: "installatie_foto_pv",
        remote_url: installatie.installatieFotoPvOnline || installatie.installatieFotoPv || null,
        sort_order: 2,
      },
    ];

    return candidates
      .filter((candidate) => !!candidate.remote_url)
      .map((candidate) => ({
        id: candidate.id,
        context_label: candidate.context_label,
        remote_url: resolveStorageUrl(String(candidate.remote_url)),
        sort_order: candidate.sort_order,
        created_at: null,
        updated_at: null,
        locatie_id: null,
        isFallback: true,
      }));
  };

  async function fetchRapportDetailFotos(
    rapportId: string,
    locatieId?: string | null,
    installatie?: Record<string, any> | null,
  ): Promise<RapportDetailFoto[]> {
    const { data, error } = await supabase
      .schema("rapport_details")
      .from("fotos")
      .select(
        `
        id,
        remote_url,
        context_label,
        sort_order,
        created_at,
        updated_at,
        locatie_id,
        verdeler_id,
        constatering_id,
        omvormer_id,
        meldformulier_id,
        internal_locatie_id
      `,
      )
      .eq("rapport_id", rapportId)
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: true });

    if (error) {
      console.error("fetchRapportDetailFotos error:", error.message, error.details, error.hint, error.code);
      captureError(error, { stage: "fetchRapportDetailFotos", rapportId });
      return getLegacyInstallatieFotos(installatie);
    }

    const detailFotos = (data || [])
      .filter((row: any) => isRapportLevelFoto(row) && isDetailFotoLabel(row.context_label))
      .map(normalizeDetailFoto)
      .sort(sortDetailFotos);

    const hasRapportFotoBoven = detailFotos.some((foto) => matchesFotoContextLabel(foto.context_label, "foto_boven"));

    if (!hasRapportFotoBoven && locatieId) {
      const { data: fallbackFoto, error: fallbackError } = await supabase
        .schema("rapport_details")
        .from("fotos")
        .select("id, remote_url, context_label, sort_order, created_at, updated_at, locatie_id")
        .eq("locatie_id", locatieId)
        .is("rapport_id", null)
        .order("updated_at", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (fallbackError) {
        console.error("fetchRapportDetailFotos fallback error:", fallbackError);
      } else if (fallbackFoto?.remote_url) {
        detailFotos.unshift(
          normalizeDetailFoto({
            ...fallbackFoto,
            id: `fallback-${fallbackFoto.id}`,
            isFallback: true,
          }),
        );
      }
    }

    const hasInstallatieFotos = detailFotos.some((foto) => isInstallatieFotoLabel(foto.context_label));
    if (!hasInstallatieFotos) {
      detailFotos.push(...getLegacyInstallatieFotos(installatie));
    }

    return detailFotos.sort(sortDetailFotos);
  }

  // Function to handle realtime updates
  const handleRealtimeUpdate = async (payload: RealtimePayload) => {
    const { new: newRapport, eventType } = payload;

    if (eventType === "UPDATE" && currentRapport.value?.id === newRapport.id) {
      const suppressUntil = suppressRefetchUntilByRapportId.get(newRapport.id) || 0;
      if (Date.now() < suppressUntil) {
        if (currentRapport.value) {
          Object.assign(currentRapport.value as any, newRapport as any);
        }

        const index = rapporten.value.findIndex((r) => r.id === newRapport.id);
        if (index !== -1) {
          Object.assign(rapporten.value[index] as any, newRapport as any);
        }

        return;
      }

      suppressRefetchUntilByRapportId.delete(newRapport.id);

      // Always fetch full data to ensure we have the complete rapport with relations
      const { data: fullRapport, error } = await supabase
        .from("rapporten")
        .select(
          `
          *,
          offertes (
            id,
            offerteNr,
            locatie_id,
            locatie:locatie_id(
              naam,
              straat,
              huisnr,
              plaats
            )
          )
        `,
        )
        .eq("id", newRapport.id)
        .single();

      if (!error && fullRapport) {
        const currentConstateringen = Array.isArray((currentRapport.value as any)?.constateringen)
          ? ((currentRapport.value as any).constateringen as any[])
          : [];

        // Re-fetch constateringen from rapport_details schema
        const constateringen = await fetchConstateringen(newRapport.id);
        const detailFotos = await fetchRapportDetailFotos(
          newRapport.id,
          getRapportLocatieId(fullRapport as any),
          ((fullRapport as any).installatie || null) as Record<string, any> | null,
        );

        const mergedConstateringen = (constateringen as any[]).map((incoming: any) => {
          const existing = currentConstateringen.find((c: any) => c.id === incoming.id);
          if (!existing) return incoming;

          const existingFotos = Array.isArray(existing.fotos) ? existing.fotos : [];
          const incomingFotos = Array.isArray(incoming.fotos) ? incoming.fotos : [];

          const normalizedIncomingUrls = new Set(
            incomingFotos.map((f: any) => resolveStorageUrl(f?.url || "")).filter((u: string) => !!u),
          );

          const preservedExistingFotos = existingFotos.filter((f: any) => {
            const normalizedUrl = resolveStorageUrl(f?.url || "");
            return normalizedUrl && !normalizedIncomingUrls.has(normalizedUrl);
          });

          return {
            ...incoming,
            fotos: [...incomingFotos, ...preservedExistingFotos],
          };
        });

        currentRapport.value = {
          ...fullRapport,
          constateringen: mergedConstateringen,
          detailFotos,
        } as any as RapportWithOfferte;

        // Also update in the rapporten list if present
        const index = rapporten.value.findIndex((r) => r.id === fullRapport.id);
        if (index !== -1) {
          const updatedRapport: any = fullRapport;
          rapporten.value[index] = updatedRapport;
        }
      } else {
        console.error("Error fetching full rapport data:", error);
        captureError(error, { stage: "handleRealtimeUpdate", rapportId: newRapport.id });
      }
    }
  };

  // Function to subscribe to realtime updates for a specific rapport
  const subscribeToRapport = async (rapportId: string) => {
    // Clean up any existing subscription
    if (realtimeSubscription) {
      realtimeSubscription.unsubscribe();
    }

    // Subscribe to changes on the rapporten table for this specific rapport
    realtimeSubscription = supabase
      .channel(`rapport-${rapportId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "rapporten",
          filter: `id=eq.${rapportId}`,
        },
        handleRealtimeUpdate,
      )
      .subscribe((_status) => {
        console.log("[rapporten] realtime subscription status:", _status);
      });
  };

  // Function to unsubscribe from realtime updates
  const unsubscribeFromRapport = () => {
    if (realtimeSubscription) {
      realtimeSubscription.unsubscribe();
      realtimeSubscription = null;
    }
    if (constateringenSubscription) {
      constateringenSubscription.unsubscribe();
      constateringenSubscription = null;
    }
  };

  async function fetchRapporten(): Promise<RapportWithOfferte[]> {
    try {
      loading.value = true;
      error.value = null;
      const { data, error: fetchError } = await supabase
        .from("rapporten")
        .select(
          `
          id,
          inspectieRapportNr,
          inspectieStatus,
          locatie_snapshot,
          created_at,
          offerte_id
        `,
        )
        .order("created_at", { ascending: false });

      if (fetchError) throw fetchError;

      const typedData = (data || []) as RapportWithOfferte[];
      rapporten.value = typedData;
      return typedData;
    } catch (err) {
      console.error("Error fetching rapporten:", err);
      error.value = "Er is een fout opgetreden bij het ophalen van de rapporten";
      toast.error(error.value, err);
      return [];
    } finally {
      loading.value = false;
    }
  }

  // Map rapport_details.constateringen snake_case to camelCase for the Constatering component
  function mapConstatering(row: any) {
    const fotos = (row.fotos || []).sort((a: any, b: any) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    const mappedFotos = fotos.map((f: any) => ({
      id: f.id,
      url: f.remote_url ? resolveStorageUrl(f.remote_url) : f.remote_url,
      label: f.context_label,
    }));

    // Keep compatibility for existing UI code paths while source of truth is fotos[]
    // Context labels use prefix patterns: 'constatering_foto_1', 'constatering_foto_2', etc.
    // Herstel photos use 'constatering_herstel_*'
    const originalFotos = mappedFotos.filter((f: any) => f.label?.startsWith("constatering_foto"));
    const herstelFotos = mappedFotos.filter((f: any) => f.label?.startsWith("constatering_herstel"));
    const foto = originalFotos[0]?.url || null;
    const hFoto = herstelFotos[0]?.url || null;

    // Parse herstel_communicatie
    let herstelCommunicatie: HerstelBericht[] = [];
    if (row.herstel_communicatie) {
      if (Array.isArray(row.herstel_communicatie)) {
        herstelCommunicatie = row.herstel_communicatie;
      } else if (typeof row.herstel_communicatie === "string") {
        try {
          const parsed = JSON.parse(row.herstel_communicatie);
          herstelCommunicatie = Array.isArray(parsed) ? parsed : [];
        } catch {
          herstelCommunicatie = [];
        }
      }
    }

    const mapped = {
      id: row.id,
      rapport_id: row.rapport_id,
      internal_locatie_id: row.internal_locatie_id || null,
      volgnummer: row.volgnummer,
      beoordeling: row.beoordeling,
      hersteld: row.hersteld,
      vereist_herinspectie: Boolean(row.vereist_herinspectie),
      meerdere_locaties: Boolean(row.meerdere_locaties),
      rapport_herinspectie_id: row.rapport_herinspectie_id || null,

      opmerkingen: row.opmerkingen,
      constatering: row.constatering,
      inspecties: row.inspecties,
      lastUpdated: row.last_updated,
      locatie: row.internal_locaties?.naam || "",
      herstelStatus: row.herstel_status || null,
      herstelCommunicatie,
      // Source of truth for photos
      fotos: mappedFotos,
      // Backward compatibility
      foto,
      hFoto,
    };

    console.log("[rapporten] mapConstatering:", {
      id: row?.id,
      last_updated: row?.last_updated,
      row_fotos_count: Array.isArray(row?.fotos) ? row.fotos.length : 0,
      originalFotos_count: originalFotos.length,
      herstelFotos_count: herstelFotos.length,
      mapped_foto: mapped.foto,
      mapped_hFoto: mapped.hFoto,
      mapped_fotos: mapped.fotos,
    });

    return mapped;
  }

  async function fetchConstateringen(rapportId: string) {
    const { data, error } = await supabase
      .schema("rapport_details")
      .from("constateringen")
      .select(
        `
        *,
        fotos(id, remote_url, context_label, sort_order),
        internal_locaties(naam)
      `,
      )
      .eq("rapport_id", rapportId)
      .order("volgnummer", { ascending: true });

    if (error) {
      console.error("fetchConstateringen error:", error.message, error.details, error.hint, error.code);
      captureError(error, { stage: "fetchConstateringen", rapportId });
      return [];
    }

    console.log("[rapporten] fetchConstateringen raw rows:", {
      rapportId,
      rows: Array.isArray(data)
        ? data.map((row: any) => ({
          id: row?.id,
          last_updated: row?.last_updated,
          fotos_count: Array.isArray(row?.fotos) ? row.fotos.length : 0,
          fotos: Array.isArray(row?.fotos)
            ? row.fotos.map((f: any) => ({ id: f?.id, context_label: f?.context_label, remote_url: f?.remote_url }))
            : [],
        }))
        : [],
    });

    const mapped = (data || []).map(mapConstatering);

    console.log("[rapporten] fetchConstateringen mapped rows:", {
      rapportId,
      mapped: mapped.map((c: any) => ({
        id: c.id,
        lastUpdated: c.lastUpdated,
        fotosCount: Array.isArray(c.fotos) ? c.fotos.length : 0,
        fotos: Array.isArray(c.fotos) ? c.fotos.map((f: any) => ({ id: f?.id, label: f?.label, url: f?.url })) : [],
      })),
    });

    return mapped;
  }

  async function fetchRapportById(id: string): Promise<RapportWithOfferte | null> {
    try {
      const { data, error } = await supabase
        .from("rapporten")
        .select(
          `
          *,
          offerte:offertes (
            id,
            offerteNr,
            locatie_id
          )
        `,
        )
        .eq("id", id)
        .single();

      if (error) {
        console.error("Store: Error fetching rapport:", error);
        throw error;
      }

      // Fetch constateringen from rapport_details schema
      const constateringen = await fetchConstateringen(id);
      const detailFotos = await fetchRapportDetailFotos(
        id,
        getRapportLocatieId(data as any),
        ((data as any).installatie || null) as Record<string, any> | null,
      );

      const typedData = { ...data, constateringen, detailFotos } as any as RapportWithOfferte;
      currentRapport.value = typedData;

      // Subscribe to realtime updates after fetching
      await subscribeToRapport(id);

      return typedData;
    } catch (error) {
      console.error("Store: Error in fetchRapportById:", error);
      toast.error("Er is een fout opgetreden bij het ophalen van het rapport", error);
      return null;
    }
  }

  async function updateRapport(id: string, updates: Partial<Rapport>): Promise<RapportWithOfferte | null> {
    try {
      // Remove any relationship data before updating
      const { offerte, ...updateData } = updates as any;

      const { data, error } = await supabase
        .from("rapporten")
        .update(updateData)
        .eq("id", id)
        .select(
          `
          *,
          offerte:offertes (
            id,
            offerteNr,
            locatie_id
          )
        `,
        )
        .single();

      if (error) {
        console.error("Store: Error updating rapport:", error);
        throw error;
      }

      // console.log('Store: Update response:', data)
      // console.log('Store: Current signature URL:', data?.installateur?.handtekening)

      // Update the current rapport in the store
      const typedData = {
        ...data,
        constateringen: (currentRapport.value as any)?.constateringen,
        detailFotos: (currentRapport.value as any)?.detailFotos,
      } as any as RapportWithOfferte;
      currentRapport.value = typedData;
      // console.log('Store: Updated currentRapport:', currentRapport.value)

      toast.success("Rapport succesvol bijgewerkt");
      return typedData;
    } catch (error) {
      console.error("Store: Error in updateRapport:", error);
      const message = error instanceof Error ? error.message : null;
      toast.error(
        message
          ? `Er is een fout opgetreden bij het bijwerken van het rapport: ${message}`
          : "Er is een fout opgetreden bij het bijwerken van het rapport",
        error,
      );
      return null;
    }
  }

  interface ConstateringUpdate {
    id?: string;

    hersteld?: boolean;
  }

  async function updateConstatering(_rapportId: string, constatering: ConstateringUpdate, index: number) {
    console.group(`[rapporten store] updateConstatering`);
    console.log("[updateConstatering] called with:", {
      _rapportId,
      constatering_id: constatering.id,

      constatering_hersteld: constatering.hersteld,
      constatering_keys: Object.keys(constatering),
      index,
    });
    try {
      // Build update payload mapping camelCase to snake_case
      const updateData: Record<string, any> = {};

      if (constatering.hersteld !== undefined) updateData.hersteld = constatering.hersteld;

      console.log("[updateConstatering] updateData payload (snake_case):", updateData);
      console.log("[updateConstatering] NOTE: fotos are stored in rapport_details.fotos, not on constateringen");

      // Get the constatering ID from the local state
      const constateringenArray = (currentRapport.value as any)?.constateringen;
      const constateringId = constatering.id || constateringenArray?.[index]?.id;
      if (!constateringId) {
        throw new Error("Could not determine constatering ID");
      }
      console.log("[updateConstatering] constateringId:", constateringId);

      console.log("[updateConstatering] sending supabase update...");
      const { data, error } = await supabase
        .schema("rapport_details")
        .from("constateringen")
        .update(updateData)
        .eq("id", constateringId)
        .select()
        .single();

      if (error) {
        console.error("[updateConstatering] supabase error:", error);
        throw error;
      }
      console.log("[updateConstatering] supabase response data:", data);

      // Update local state with mapped data, but preserve photo fields.
      // The .update().select() response doesn't include the `fotos` join,
      // so mapConstatering(data) would miss photo URLs.
      if (currentRapport.value && Array.isArray((currentRapport.value as any).constateringen)) {
        const localIndex = (currentRapport.value as any).constateringen.findIndex((c: any) => c.id === constateringId);
        console.log("[updateConstatering] updating local state at index:", localIndex);
        if (localIndex !== -1) {
          const mapped = mapConstatering(data);
          console.log("[updateConstatering] mapped constatering:", mapped);
          const existing = (currentRapport.value as any).constateringen[localIndex];
          // Preserve photo-related fields from local state since the DB
          // response doesn't include the fotos join
          const { hFoto: _h, foto: _f, fotos: _fs, ...mappedWithoutPhotos } = mapped;
          console.log("[updateConstatering] preserving local photo fields:", {
            fotosCount: existing.fotos?.length,
          });
          (currentRapport.value as any).constateringen[localIndex] = {
            ...existing,
            ...mappedWithoutPhotos,
          };
        }
      }

      toast.success("Constatering succesvol bijgewerkt");
      console.groupEnd();
      return data;
    } catch (error) {
      console.error("[updateConstatering] ERROR:", error);
      toast.error("Er is een fout opgetreden bij het bijwerken van de constatering", error);
      console.groupEnd();
      return null;
    }
  }

  async function deleteConstatering(rapportId: string, constateringId: string): Promise<boolean> {
    try {
      // Delete from the normalized rapport_details.constateringen table
      const { error } = await supabase
        .schema("rapport_details")
        .from("constateringen")
        .delete()
        .eq("id", constateringId)
        .eq("rapport_id", rapportId);

      if (error) throw error;

      // Update local state — remove the constatering from the assembled array
      if (currentRapport.value?.id === rapportId) {
        const existing = (currentRapport.value as any)?.constateringen;
        if (Array.isArray(existing)) {
          (currentRapport.value as any).constateringen = existing.filter((c: any) => c.id !== constateringId);
        }
      }

      toast.success("Constatering succesvol verwijderd");
      return true;
    } catch (error) {
      console.error("Error deleting constatering:", error);
      toast.error("Er is een fout opgetreden bij het verwijderen van de constatering", error);
      return false;
    }
  }

  async function appendHerstelBericht(
    constateringId: string,
    bericht: HerstelBericht,
    newStatus: "pending" | "accepted" | "rejected",
  ) {
    try {
      // Read current communicatie
      const { data: current, error: readError } = await supabase
        .schema("rapport_details")
        .from("constateringen")
        .select("herstel_communicatie")
        .eq("id", constateringId)
        .single();

      if (readError) throw readError;

      let existing: HerstelBericht[] = [];
      if (current?.herstel_communicatie) {
        existing = Array.isArray(current.herstel_communicatie)
          ? (current.herstel_communicatie as unknown as HerstelBericht[])
          : [];
      }

      const updatedCommunicatie = [...existing, bericht];

      const { data, error: updateError } = await supabase
        .schema("rapport_details")
        .from("constateringen")
        .update({
          herstel_communicatie: updatedCommunicatie as any,
          herstel_status: newStatus,
        })
        .eq("id", constateringId)
        .select()
        .single();

      if (updateError) throw updateError;

      // Update local state
      if (currentRapport.value && Array.isArray((currentRapport.value as any).constateringen)) {
        const idx = (currentRapport.value as any).constateringen.findIndex((c: any) => c.id === constateringId);
        if (idx !== -1) {
          const mapped = mapConstatering(data);
          const existingLocal = (currentRapport.value as any).constateringen[idx];
          const { hFoto: _h, foto: _f, fotos: _fs, ...mappedWithoutPhotos } = mapped;
          (currentRapport.value as any).constateringen[idx] = {
            ...existingLocal,
            ...mappedWithoutPhotos,
          };
        }
      }

      return data;
    } catch (error) {
      console.error("Error appending herstel bericht:", error);
      toast.error("Er is een fout opgetreden bij het opslaan van het bericht", error);
      return null;
    }
  }

  async function generatePDF(_id: string): Promise<void> {
    // Implement PDF generation logic here
    // This is a placeholder for future implementation
    // console.log('PDF generation for rapport', _id)
  }

  return {
    rapporten,
    loading,
    error,
    currentRapport,
    fetchRapporten,
    fetchRapportById,
    updateRapport,
    updateConstatering,
    deleteConstatering,
    generatePDF,
    appendHerstelBericht,
    unsubscribeFromRapport,
    suppressRealtimeRefetch,
  };
});
